import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ShieldCheck, AlertCircle, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import {
    api, getDeviceId, setToken, getRememberedIdentity, setRememberedIdentity,
} from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PinDots, PinKeypad } from '@/components/PinKeypad';

/**
 * Phase 17 — PIN Unlock screen.
 *
 * Gated in front of the rest of the app whenever a returning user opens
 * Nia on a device that has a PIN registered. On success we accept the
 * fresh JWT minted by `/auth/pin/verify` and continue to home.
 *
 * Error states:
 *   - Wrong PIN → shake dots, surface attempts remaining (after 1st wrong)
 *   - 5 wrong attempts → 30-min lockout countdown + force OTP fallback
 *   - "Forgot PIN" → logout + redirect to email/phone OTP login
 */
export default function PinUnlock() {
    const { user, logout, completePinGate, login, pinIdentity, abandonPinUnlock } = useAuth();
    const nav = useNavigate();
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [attemptsRemaining, setAttemptsRemaining] = useState(null);
    const [errorShake, setErrorShake] = useState(false);
    const [lockedUntil, setLockedUntil] = useState(null);
    const [identity, setIdentity] = useState(null);
    const deviceIdRef = useRef(null);

    useEffect(() => {
        (async () => {
            deviceIdRef.current = await getDeviceId();
            // Greet user by name. Priority order:
            //   1. live `user` from a valid JWT (best — full freshness)
            //   2. `pinIdentity` from /auth/pin/device-status (cold start)
            //   3. local "remembered" identity (offline / legacy fallback)
            const cached = await getRememberedIdentity();
            const src = user
                ? { id: user.id, name: user.name, email: user.email, role: user.role }
                : pinIdentity
                    ? { id: pinIdentity.id, name: pinIdentity.name, role: pinIdentity.role,
                        email_masked: pinIdentity.email_masked, phone_masked: pinIdentity.phone_masked }
                    : cached;
            setIdentity(src);
        })();
    }, [user, pinIdentity]);

    // Countdown timer when locked
    const [secondsLeft, setSecondsLeft] = useState(null);
    useEffect(() => {
        if (!lockedUntil) { setSecondsLeft(null); return; }
        const tick = () => {
            const ms = new Date(lockedUntil).getTime() - Date.now();
            if (ms <= 0) {
                setLockedUntil(null); setSecondsLeft(null);
                setAttemptsRemaining(null);
            } else {
                setSecondsLeft(Math.ceil(ms / 1000));
            }
        };
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [lockedUntil]);

    const submit = async (full) => {
        if (lockedUntil) return;
        if (!deviceIdRef.current) deviceIdRef.current = await getDeviceId();
        setBusy(true);
        try {
            const payload = {
                pin: full,
                device_id: deviceIdRef.current,
            };
            if (identity?.id) payload.user_id = identity.id;
            else if (identity?.email) payload.email = identity.email;
            const r = await api.pinVerify(payload);
            if (r.data?.ok) {
                setToken(r.data.token);
                if (r.data.user) setRememberedIdentity(r.data.user);
                await login(r.data.token, r.data.user, r.data.csrf_token);
                completePinGate();
                nav('/', { replace: true });
                return;
            }
            // Wrong PIN — surface remaining attempts
            const remaining = r.data?.attempts_remaining;
            setAttemptsRemaining(remaining);
            setErrorShake(true);
            setTimeout(() => setErrorShake(false), 500);
            setPin('');
            toast.error(remaining > 0 ? `Wrong PIN. ${remaining} attempt${remaining === 1 ? '' : 's'} left.` : 'Wrong PIN.');
        } catch (e) {
            const status = e?.response?.status;
            const data = e?.response?.data;
            if (status === 423) {
                const lu = (typeof data?.detail === 'object' && data.detail?.locked_until) || data?.locked_until;
                if (lu) setLockedUntil(lu);
                toast.error('Too many wrong PINs. Locked for 30 minutes.');
                setPin('');
            } else if (status === 404) {
                toast.error("This device isn't recognized — please sign in again.");
                forgotPin();
            } else if (status === 429) {
                toast.error('Too many attempts. Please wait a minute.');
                setPin('');
            } else {
                toast.error(data?.detail || 'Could not verify PIN');
                setPin('');
            }
        } finally { setBusy(false); }
    };

    const onKey = (k) => {
        if (busy || lockedUntil) return;
        if (k === 'del') return setPin((p) => p.slice(0, -1));
        if (pin.length >= 4) return;
        const next = pin + k;
        setPin(next);
        if (next.length === 4) setTimeout(() => submit(next), 120);
    };

    const forgotPin = async () => {
        try {
            const did = deviceIdRef.current || (await getDeviceId());
            // Best-effort: clear our local PIN record so the next OTP login
            // re-prompts PinSetup cleanly. Server-side row is also cleared.
            try {
                await api.pinClear(did);
            } catch (e) {
                // Acceptable failure modes: user JWT expired, server 401,
                // or no PIN row server-side. We continue the local logout
                // either way so the user can re-authenticate.
                console.warn('[pin-unlock] forgotPin: pinClear failed (continuing logout):', e?.message || e);
            }
        } catch (e) {
            console.warn('[pin-unlock] forgotPin: device-id lookup failed:', e?.message || e);
        }
        await logout();
        completePinGate();
        abandonPinUnlock();
        setRememberedIdentity(null);
        nav('/', { replace: true });
    };

    const switchAccount = async () => {
        await logout();
        completePinGate();
        abandonPinUnlock();
        setRememberedIdentity(null);
        nav('/', { replace: true });
    };

    const firstName = (identity?.name || '').split(' ')[0] || 'there';

    return (
        <div className="nia-page" data-testid="pin-unlock">
            <div className="mobile-shell" style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
                <header style={{ padding: '36px 18px 8px', textAlign: 'center' }}>
                    <div style={{ width: 56, height: 56, borderRadius: 18, background: 'var(--nia-primary-tint)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nia-primary)' }}>
                        <Lock size={24} />
                    </div>
                    <h1 className="font-display" style={{ fontSize: 24, fontWeight: 900, margin: '12px 0 0' }} data-testid="pin-unlock-greeting">
                        {lockedUntil ? 'PIN locked' : `Welcome back, ${firstName}`}
                    </h1>
                    <div style={{ color: 'var(--nia-subtext)', fontSize: 13, marginTop: 6, lineHeight: 1.55, padding: '0 12px' }}>
                        {lockedUntil
                            ? 'Too many wrong attempts. Please wait or sign in with email or phone.'
                            : 'Enter your 4-digit PIN to continue.'}
                    </div>
                    {!lockedUntil && (identity?.email_masked || identity?.phone_masked) && (
                        <div style={{ color: 'var(--nia-subtext)', fontSize: 11, marginTop: 4, opacity: 0.8 }} data-testid="pin-unlock-identity-hint">
                            {identity.email_masked || identity.phone_masked}
                        </div>
                    )}
                </header>

                <div style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: 18, gap: 22,
                    animation: errorShake ? 'pin-shake 360ms ease' : 'none',
                }}>
                    <style>{`@keyframes pin-shake { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-8px); } 40%,80% { transform: translateX(8px); } }`}</style>
                    <PinDots length={4} filled={pin.length} error={errorShake} testid="pin-unlock-dots" />

                    {lockedUntil && secondsLeft != null && (
                        <div data-testid="pin-unlock-lockout" style={{
                            padding: '10px 14px', background: '#FCE7E4', color: '#7A1A0F',
                            borderRadius: 10, fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}>
                            <AlertCircle size={14} /> Unlock available in {formatCountdown(secondsLeft)}
                        </div>
                    )}

                    {!lockedUntil && attemptsRemaining != null && attemptsRemaining < 5 && (
                        <div data-testid="pin-unlock-attempts-warning" style={{
                            padding: '8px 12px', background: 'var(--nia-bg-cream)', color: '#7A5300',
                            borderRadius: 10, fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}>
                            <AlertCircle size={12} /> {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} left
                        </div>
                    )}

                    <PinKeypad onKey={onKey} disabled={busy || !!lockedUntil} testid="pin-unlock-keypad" />
                </div>

                <footer style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <button onClick={forgotPin} className="nia-btn-secondary"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                        data-testid="pin-unlock-forgot">
                        Forgot PIN? Sign in with email or phone
                    </button>
                    {identity?.email && (
                        <button onClick={switchAccount}
                            style={{ background: 'none', border: 'none', color: 'var(--nia-subtext)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                            data-testid="pin-unlock-switch-account">
                            <LogOut size={11} /> Not {firstName}? Switch account
                        </button>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--nia-subtext)', lineHeight: 1.55, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <ShieldCheck size={14} color="var(--nia-primary)" style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>PIN works on this device only. Nia never moves your funds — agents handle the cash off-platform.</span>
                    </div>
                </footer>
            </div>
        </div>
    );
}

function formatCountdown(seconds) {
    const m = Math.floor(seconds / 60).toString();
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
}
